import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import axios from 'axios'

export const Route = createFileRoute('/forgot-password')({
    component: ForgotPasswordComponent,
})

function ForgotPasswordComponent() {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSubmitted, setIsSubmitted] = useState(false)
    const navigate = useNavigate()

    const onFormSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
        setIsSubmitting(true)
        try {
            evt.preventDefault()
            const data = new FormData(evt.currentTarget)
            const username = data.get('username')?.toString()

            if (!username) {
                console.error('Username field is empty')
                return
            }

            const response = await axios.post('/auth/forgot', { userName: username })

            if (response.status !== 200) {
                throw new Error('Failed to process forgot password request')
            }

            setIsSubmitted(true)
        } catch (error) {
            console.error('Error processing forgot password:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
            <div className="flex w-full max-w-sm flex-col gap-6">
                <a href="#" className="flex items-center gap-2 self-center font-medium">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md text-primary-foreground">
                        <img src="favicon-32x32.png" className="size-8" />
                    </div>
                    CoolFocus
                </a>
                <div className="flex flex-col gap-6">
                    <Card>
                        <CardHeader className="text-center">
                            <CardTitle className="text-xl">Forgot Password</CardTitle>
                            <CardDescription>
                                Enter your username and we'll send you a password reset link
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {isSubmitted ? (
                                <div className="grid gap-6">
                                    <div className="text-center text-sm text-muted-foreground">
                                        If an account exists with that username, we've sent a password reset link to the associated email address.
                                    </div>
                                    <Button
                                        variant="outline"
                                        className="w-full"
                                        onClick={() => navigate({ to: '/sign-in' })}
                                    >
                                        Return to Sign In
                                    </Button>
                                </div>
                            ) : (
                                <form onSubmit={onFormSubmit}>
                                    <div className="grid gap-6">
                                        <div className="grid gap-2">
                                            <Label htmlFor="username">Username</Label>
                                            <Input
                                                id="username"
                                                name="username"
                                                type="text"
                                                placeholder="Enter your username"
                                                required
                                                autoFocus
                                                autoComplete="username"
                                            />
                                        </div>
                                        <Button type="submit" className="w-full" disabled={isSubmitting}>
                                            Send Reset Link
                                        </Button>
                                        <div className="text-center text-sm">
                                            Remember your password?{" "}
                                            <a
                                                href="#"
                                                className="underline underline-offset-4"
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    navigate({ to: '/sign-in' })
                                                }}
                                            >
                                                Sign in
                                            </a>
                                        </div>
                                    </div>
                                </form>
                            )}
                        </CardContent>
                    </Card>
                    <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 [&_a]:hover:text-primary">
                        By clicking continue, you agree to our <a href="#">Terms of Service</a>{" "}
                        and <a href="#">Privacy Policy</a>.
                    </div>
                </div>
            </div>
        </div>
    )
} 
